import { useEffect, useRef } from 'react';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { App } from '@capacitor/app';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface FCMTokenPlugin {
  getToken(): Promise<{ token: string }>;
}

// Native plugin (ios/App/App/FCMTokenPlugin.swift) that returns the real Firebase
// FCM token. Capacitor's own PushNotifications 'registration' event only ever
// delivers the raw APNs device token on iOS, which the FCM-only send pipeline
// (supabase/functions/send-push-notification) can't deliver to.
const FCMToken = registerPlugin<FCMTokenPlugin>('FCMToken');

export const usePushNotifications = () => {
  const { user } = useAuth();
  const registeredRef = useRef(false);
  const isRegisteringRef = useRef(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !user) {
      return;
    }

    // Prevent duplicate registration
    if (registeredRef.current || isRegisteringRef.current) {
      return;
    }

    const registerToken = async (token: string, platform: string) => {
      console.log('[PushNotifications] Registering push token:', { 
        tokenPreview: token.substring(0, 30) + '...', 
        tokenLength: token.length,
        platform 
      });

      // FCM tokens are typically 150+ characters, APNs device tokens are 64 hex chars
      // If we get a short token, it's likely an APNs token which won't work with FCM
      if (token.length < 100) {
        console.warn('[PushNotifications] Token appears to be APNs format, not FCM. Waiting for FCM token...');
        return;
      }

      try {
        const { error } = await supabase
          .from('push_tokens')
          .upsert(
            {
              user_id: user.id,
              token,
              platform,
            },
            {
              onConflict: 'user_id,token',
            }
          );

        if (error) {
          console.error('[PushNotifications] Error registering push token:', error);
        } else {
          console.log('[PushNotifications] Push token registered successfully');
        }
      } catch (err) {
        console.error('[PushNotifications] Error in registerToken:', err);
      }
    };

    const initPushNotifications = async () => {
      if (isRegisteringRef.current) return;
      isRegisteringRef.current = true;

      try {
        const permStatus = await PushNotifications.checkPermissions();
        
        let hasPermission = permStatus.receive === 'granted';
        
        if (permStatus.receive === 'prompt') {
          const result = await PushNotifications.requestPermissions();
          hasPermission = result.receive === 'granted';
        }
        
        if (!hasPermission) {
          console.log('[PushNotifications] Push notification permission denied');
          return;
        }

        if (Capacitor.getPlatform() === 'android') {
          // send-push-notification always targets channelId "messages" (Android 8+
          // silently drops any notification whose channel doesn't exist on-device,
          // with no error anywhere in the pipeline), so it must exist before any
          // message can arrive.
          try {
            await PushNotifications.createChannel({
              id: 'messages',
              name: 'Messages',
              description: 'New chat message notifications',
              importance: 5,
              visibility: 1,
              sound: 'default',
            });
          } catch (err) {
            console.error('[PushNotifications] Failed to create Android notification channel:', err);
          }
        }

        await PushNotifications.register();
        registeredRef.current = true;
        console.log('[PushNotifications] Push notifications registered');

        if (Capacitor.getPlatform() === 'ios') {
          // Android's FCM token arrives via the 'registration' event listener below.
          // iOS needs the real FCM token fetched separately, since that event only
          // ever carries the raw APNs device token there.
          try {
            const { token } = await FCMToken.getToken();
            await registerToken(token, 'ios');
          } catch (err) {
            console.error('[PushNotifications] Failed to get FCM token on iOS:', err);
          }
        }
      } catch (err) {
        console.error('[PushNotifications] Error registering push notifications:', err);
      } finally {
        isRegisteringRef.current = false;
      }
    };

    // Handle token registration from Capacitor. On iOS this only ever carries the
    // raw APNs device token, which is handled separately above via FCMToken.getToken().
    const tokenListener = PushNotifications.addListener('registration', (token: Token) => {
      const currentPlatform = Capacitor.getPlatform();
      if (currentPlatform === 'ios') {
        return;
      }
      console.log('[PushNotifications] Received registration token from Capacitor:', {
        tokenPreview: token.value.substring(0, 30) + '...',
        tokenLength: token.value.length,
        platform: currentPlatform
      });
      registerToken(token.value, currentPlatform);
    });

    // Handle registration errors
    const errorListener = PushNotifications.addListener('registrationError', (error) => {
      console.error('[PushNotifications] Push registration error:', error);
    });

    // Handle received notifications (app in foreground)
    const notificationListener = PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        console.log('[PushNotifications] Push notification received:', notification);
      }
    );

    // Handle notification tap (app opened from notification)
    const actionListener = PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action: ActionPerformed) => {
        console.log('[PushNotifications] Push notification action:', action);
        const chatId = action.notification.data?.chatId;
        if (chatId) {
          window.location.href = `/chat/${chatId}`;
        }
      }
    );

    // Initialize push notifications
    initPushNotifications();

    // Clear badge count when app comes to foreground
    const clearBadge = async () => {
      try {
        await PushNotifications.removeAllDeliveredNotifications();
        // On iOS, setting badge to 0 clears it
        console.log('[PushNotifications] Cleared badge and delivered notifications');
      } catch (err) {
        console.error('[PushNotifications] Error clearing badge:', err);
      }
    };

    // Clear badge on app resume
    const appStateListener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        clearBadge();
      }
    });

    // Also clear on initial load
    clearBadge();

    return () => {
      tokenListener.then(l => l.remove());
      errorListener.then(l => l.remove());
      notificationListener.then(l => l.remove());
      actionListener.then(l => l.remove());
      appStateListener.then(l => l.remove());
    };
  }, [user?.id]); // Only depend on user.id to prevent re-runs

  const unregister = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('push_tokens')
        .delete()
        .eq('user_id', user.id);

      if (error) {
        console.error('[PushNotifications] Error removing push tokens:', error);
      }
    } catch (err) {
      console.error('[PushNotifications] Error in unregister:', err);
    }
  };

  return {
    unregister,
  };
};
